import React, { useState } from "react";
import {
  FormControl,
  FormLabel,
  VStack,
  FormErrorMessage,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import CustomInput from "../../components/UI/CustomInput";
import CustomButton from "../../components/UI/CustomButton";
import { useFormTheme } from "../../hooks/useFormTheme";
import { FORM_PADDING_WIDTH, PAGE_PADDING } from "../../helpers/constant";
import { useAuth } from "../../context/AuthContext";
import FormErrorBox from "../../components/UI/FormErrorBox";
import { useTranslation } from "react-i18next";
import { getLocalizedAuthErrorMessage } from "../../helpers/authErrors";

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { pageBg, textColor, errorColor } = useFormTheme();
  const toast = useToast();
  const { t } = useTranslation();
  const { changePasswordHandler, signOutHandler, isLoading } = useAuth();

  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = (field?: string, value?: string) => {
    const newErrors: Record<string, string> =
      field && hasSubmitted ? { ...errors } : {};
    const f = {
      ...form,
      ...(field && field in form
        ? { [field]: value ?? form[field as keyof typeof form] }
        : {}),
    };

    const check = (
      name: keyof typeof form,
      condition: boolean,
      msg: string,
    ) => {
      if (!field || field === name) {
        condition ? (newErrors[name] = msg) : delete newErrors[name];
      }
    };

    check(
      "oldPassword",
      !f.oldPassword,
      String(t("auth.common.oldPasswordRequired")),
    );
    check(
      "newPassword",
      f.newPassword.length < 6,
      String(t("auth.common.passwordTooShort")),
    );

    if (!field || field === "newPassword" || field === "confirmPassword") {
      if (f.confirmPassword !== f.newPassword) {
        newErrors.confirmPassword = String(t("auth.common.passwordsMustMatch"));
      } else {
        delete newErrors.confirmPassword;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!validate()) return;

    setLoading(true);
    try {
      await changePasswordHandler(form.oldPassword, form.newPassword);
      await signOutHandler();
      toast({
        title: String(t("auth.changePassword.successTitle")),
        description: String(t("auth.changePassword.successDescription")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      navigate("/login", { replace: true });
    } catch (error) {
      toast({
        title: String(t("auth.changePassword.errorTitle")),
        description: getLocalizedAuthErrorMessage(error, t),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack
      w={{ base: "full", md: "70%", lg: "400px", "2xl": "425px" }}
      px={PAGE_PADDING}
      mx="auto"
      align="stretch"
      justifyContent="center"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing="24px" align="stretch" w={FORM_PADDING_WIDTH}>
          {/* Old Password */}
          <FormControl isInvalid={!!errors.oldPassword}>
            <FormLabel>{t("auth.changePassword.oldPassword")}</FormLabel>
            <CustomInput
              type="password"
              placeholder={String(
                t("auth.changePassword.oldPasswordPlaceholder"),
              )}
              value={form.oldPassword}
              error={!!errors.oldPassword}
              onChange={(e) => {
                const value = e.target.value;
                setForm({ ...form, oldPassword: value });
                if (hasSubmitted) {
                  validate("oldPassword", value);
                }
              }}
            />
            <FormErrorMessage color={errorColor}>
              {errors.oldPassword}
            </FormErrorMessage>
          </FormControl>

          {/* New Password */}
          <FormControl isInvalid={!!errors.newPassword}>
            <FormLabel>{t("auth.changePassword.newPassword")}</FormLabel>
            <CustomInput
              type="password"
              placeholder={String(
                t("auth.changePassword.newPasswordPlaceholder"),
              )}
              value={form.newPassword}
              error={!!errors.newPassword}
              onChange={(e) => {
                const value = e.target.value;
                setForm({ ...form, newPassword: value });
                if (hasSubmitted) {
                  validate("newPassword", value);
                }
              }}
            />
            <FormErrorMessage color={errorColor}>
              {errors.newPassword}
            </FormErrorMessage>
          </FormControl>

          {/* Confirm Password */}
          <FormControl isInvalid={!!errors.confirmPassword}>
            <FormLabel>{t("auth.changePassword.confirmPassword")}</FormLabel>
            <CustomInput
              type="password"
              placeholder={String(
                t("auth.changePassword.confirmPasswordPlaceholder"),
              )}
              value={form.confirmPassword}
              error={!!errors.confirmPassword}
              onChange={(e) => {
                const value = e.target.value;
                setForm({ ...form, confirmPassword: value });
                if (hasSubmitted) {
                  validate("confirmPassword", value);
                }
              }}
            />
            <FormErrorMessage color={errorColor}>
              {errors.confirmPassword}
            </FormErrorMessage>
          </FormControl>

          <FormErrorBox errors={Object.values(errors)} />

          <CustomButton type="submit" isDisabled={loading || isLoading}>
            {loading || isLoading ? (
              <Spinner size="sm" />
            ) : (
              t("auth.changePassword.submit")
            )}
          </CustomButton>
        </VStack>
      </form>
    </VStack>
  );
};

export default ChangePasswordPage;
